import React, { useEffect, useMemo, useState } from "react";

import type {
  OfferQuantityButtonType,
  QuantityButtonsProduct,
} from "@intelligems/ig-types";
import { styled } from "@mui/material";
import {
  FreeGiftBlockContainer,
  FreeGiftBlockLabel,
} from "@src/QuantityButtonsView/components/sharedComponents";
import { getFormattedButton } from "@src/QuantityButtonsView/utils";

import { QuantityButtonContentRenderer } from "../../QuantityButtonsView/types";
import { cls } from "../../util";
import { useQuantityButtonsContext } from "../context/QuantityButtonsContext";

interface ButtonRootProps {
  button: OfferQuantityButtonType;
  selected: boolean;
  renderContent: QuantityButtonContentRenderer;
  className?: string;
}

export const NewButtonRoot = ({
  button,
  selected,
  renderContent,
  className,
}: ButtonRootProps) => {
  const { calculateButtonState, onQuantityChange } =
    useQuantityButtonsContext();
  const { widget } = useQuantityButtonsContext();

  const [state, setState] = useState<{
    unitDiscount: number;
    discountedUnitPrice: string;
    originalTotalPrice: string | undefined;
    originalPricePerItem: string | undefined;
    newTotalPrice: string | undefined;
    newPricePerItem: string | undefined;
    productTitle: string | undefined;
    savedAmountPerItem: string | undefined;
    savedTotalAmount: string | undefined;
    savedPercentage: string | undefined;
    totalComparePrice: string | undefined;
  }>({
    unitDiscount: 0,
    discountedUnitPrice: "",
    newPricePerItem: "",
    newTotalPrice: "",
    originalPricePerItem: "",
    originalTotalPrice: "",
    productTitle: "",
    savedAmountPerItem: "",
    savedPercentage: "",
    savedTotalAmount: "",
    totalComparePrice: "",
  });

  const formattedButton = useMemo(() => {
    return getFormattedButton({
      button,
      discountedUnitPrice: state.discountedUnitPrice,
      newPricePerItem: state.newPricePerItem,
      newTotalPrice: state.newTotalPrice,
      originalPricePerItem: state.originalPricePerItem,
      originalTotalPrice: state.originalTotalPrice,
      productTitle: state.productTitle,
      savedAmountPerItem: state.savedAmountPerItem,
      savedTotalAmount: state.savedTotalAmount,
      savedPercentage: state.savedPercentage,
    });
  }, [state, button]);

  useEffect(() => {
    void (async () => {
      const state = await calculateButtonState(button);
      if (state) {
        setState(state);
      }
    })();
  }, [calculateButtonState, button]);

  const [hovered, setHovered] = useState<boolean>(false);
  const active = hovered || selected;

  if (widget.variant === "quantityButtonsWidgetCompact") {
    return (
      <CompactButton
        className={cls("ig-volume-widget-button", className)}
        active={active}
        selected={selected}
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
        data-quantity={button.quantity}
        onClick={() => onQuantityChange?.(button.quantity)}
      >
        {renderContent({
          button: formattedButton,
          unitDiscount: state.unitDiscount,
          mode: active ? "hover" : "extra",
          discountedUnitPrice: state.discountedUnitPrice,
          newPricePerItem: state.newPricePerItem,
          newTotalPrice: state.newTotalPrice,
          originalPricePerItem: state.originalPricePerItem,
          originalTotalPrice: state.originalTotalPrice,
          productTitle: state.productTitle,
          savedAmountPerItem: state.savedAmountPerItem,
          savedTotalAmount: state.savedTotalAmount,
          savedPercentage: state.savedPercentage,
          totalComparePrice: state.totalComparePrice,
        })}
      </CompactButton>
    );
  }
  if (
    widget.styles.appearance.layout === "column" &&
    button.freeGift?.freeGiftImage &&
    !button.freeGift.hide &&
    (!button.freeGift.showOnlyWhenButtonSelected ||
      (button.freeGift.showOnlyWhenButtonSelected && selected))
  ) {
    return (
      <ButtonGiftBlockContainer>
        <Button
          className={cls("ig-volume-widget-button", className)}
          active={active}
          selected={selected}
          onMouseOver={() => setHovered(true)}
          onMouseOut={() => setHovered(false)}
          data-quantity={button.quantity}
          onClick={() => onQuantityChange?.(button.quantity)}
          style={{
            borderBottomLeftRadius: "0px",
            borderBottomRightRadius: "0px",
            border: "none",
          }}
        >
          {renderContent({
            button: formattedButton,
            unitDiscount: state.unitDiscount,
            mode: active ? "hover" : "extra",
            discountedUnitPrice: state.discountedUnitPrice,
            newPricePerItem: state.newPricePerItem,
            newTotalPrice: state.newTotalPrice,
            originalPricePerItem: state.originalPricePerItem,
            originalTotalPrice: state.originalTotalPrice,
            productTitle: state.productTitle,
            savedAmountPerItem: state.savedAmountPerItem,
            savedTotalAmount: state.savedTotalAmount,
            savedPercentage: state.savedPercentage,
            totalComparePrice: state.totalComparePrice,
          })}
        </Button>
        <FreeGiftBlockContainer>
          <div
            style={{
              alignItems: "center",
              gap: "14px",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <img
              src={button.freeGift.freeGiftImage}
              className={"ig-quantity-free-gift-image"}
              style={{ width: "50px", borderRadius: "5px" }}
              alt={
                formattedButton?.freeGift?.freeGiftLabel || "free-gift-image"
              }
            />
            <FreeGiftBlockLabel>
              {formattedButton?.freeGift?.freeGiftLabel}
            </FreeGiftBlockLabel>
          </div>
        </FreeGiftBlockContainer>
      </ButtonGiftBlockContainer>
    );
  }
  return (
    <Button
      className={cls("ig-volume-widget-button", className)}
      active={active}
      selected={selected}
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      data-quantity={button.quantity}
      onClick={() => onQuantityChange?.(button.quantity)}
    >
      {renderContent({
        button: formattedButton,
        unitDiscount: state.unitDiscount,
        mode: active ? "hover" : "extra",
        discountedUnitPrice: state.discountedUnitPrice,
        newPricePerItem: state.newPricePerItem,
        newTotalPrice: state.newTotalPrice,
        originalPricePerItem: state.originalPricePerItem,
        originalTotalPrice: state.originalTotalPrice,
        productTitle: state.productTitle,
        savedAmountPerItem: state.savedAmountPerItem,
        savedTotalAmount: state.savedTotalAmount,
        savedPercentage: state.savedPercentage,
        totalComparePrice: state.totalComparePrice,
      })}
    </Button>
  );
};

interface ButtonProps {
  active: boolean;
  selected: boolean;
}

const CompactButton = styled("div")(({ active, selected }: ButtonProps) => {
  const { widget } = useQuantityButtonsContext();
  const borderColor =
    widget.styles.backgroundColors.buttonBorderColor || "#000";
  const cursor = active ? "pointer" : "auto";
  const borderRadius = widget.styles.appearance.cornerRadius || "5.5px";
  const activeBackgroundColor =
    widget.styles.backgroundColors.buttonActiveBackground || "#FFF";
  const inactiveBackgroundColor =
    widget.styles.backgroundColors.buttonInactiveBackground ||
    "rgba(0, 0, 0, .05)";

  return {
    boxSizing: "border-box",
    border: `solid 1px ${borderColor}`,
    backgroundColor: active ? activeBackgroundColor : inactiveBackgroundColor,
    cursor,
    height: "auto",
    maxWidth: "170px",
    borderRadius,
    padding: "10px 8px",

    position: "relative",
    "&:hover": {
      boxShadow: `inset 0 0 0 1px ${borderColor}`,
    },
    ...(selected && {
      boxShadow: `inset 0 0 0 1px ${borderColor}`,
    }),
  };
});

const Button = styled("div")(({ active, selected }: ButtonProps) => {
  const { widget } = useQuantityButtonsContext();
  const { device } = widget;
  const borderColor = widget.styles.backgroundColors.buttonBorderColor;
  const cursor = active ? "pointer" : "auto";
  const borderRadius = widget.styles.appearance.cornerRadius;
  const activeBackgroundColor =
    widget.styles.backgroundColors.buttonActiveBackground;
  const inactiveBackgroundColor =
    widget.styles.backgroundColors.buttonInactiveBackground;

  return {
    border: `solid 1px ${borderColor}`,
    boxSizing: "border-box",
    backgroundColor: active ? activeBackgroundColor : inactiveBackgroundColor,
    cursor,
    height: "auto",
    width: device === "desktop" ? "100%" : "105px",
    maxWidth: "550px",
    borderRadius,
    padding:
      widget.variant === "quantityButtonsWidgetCompact"
        ? "10px 8px"
        : "20px 12px",
    position: "relative",
    "&:hover": {
      boxShadow: `inset 0 0 0 1px ${borderColor}`,
    },
    ...(selected && {
      boxShadow: `inset 0 0 0 1px ${borderColor}`,
    }),
  };
});

const ButtonGiftBlockContainer = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  const borderColor =
    widget.styles.backgroundColors.buttonBorderColor || "#000";
  const borderRadius = widget.styles.appearance.cornerRadius;

  return {
    border: `solid 1px ${borderColor}`,
    borderRadius,

    position: "relative",
    "&:hover": {
      boxShadow: `inset 0 0 0 1px ${borderColor}`,
      cursor: "pointer",
    },
    "&:hover .ig-quantity-widget-badge": {
      ...(widget.styles.appearance.layout === "row" && {
        left: "-1px",
        width: "90%",
      }),
    },
  };
});
